// router
import router from '@/router';

// utils
import { transformToInt } from '@/utils/';

/**
 *
 * Percorrido a estrutura do script e conforme o método
 * ( add / edit ) irá constuir a estrutura do script
 * ( páginas / questões ) para ser enviado para API.
 * A estrutura do campo proveniente do formulário é a seguinte
 * - question[questionIndex]
 * - question[questionIndex]-answer[answerIndex]
 * - next-question[questionIndex]
 * - next-question[questionIndex]-answer[answerIndex]
 *
 * A estrutura a ser enviada para API é a seguinte:
 * - questions[questionIndex][question]
 * - questions[questionIndex][answers][anwsersIndex][value]
 *
 * @param {Object}  pages
 * @param {Object}  script
 * @param {Object}  fields
 * @param {Object}  initialPages
 * @category model
 * @since 1.1.0
 * @auther Tiago Martinho
 *
*/
const buildPages = {
    add: ({
        pages,
        script,
        fields,
        scriptType
    }) => {
        const isChainedScript = scriptType === 1;
        // Interação no grupo de questões, de forma a construir a estrutura base
        // para ser enviado para API.
        // nesta interação, será validado os campos de questão e de tipo de input que
        // poderá vir com este nomenclatura:
        // question - question[1]
        // type - type[1]
        script.questions.forEach( ( question, index ) => {
            const questionKey = `questions[${index}]`; // definição da key da questão que será enviada para API.
            pages[`${questionKey}[question]`] = fields[`question[${question.id}]`];

            // Se for um guião sequencial, manda a sequência.
            if ( !isChainedScript )
                pages[`${questionKey}[sequence]`] = index;

            const inputType = fields[`type[${question.id}]`] || 1;

            // Só irá enviar o tipo de input para API.
            pages[`${questionKey}[input_type_id]`] = ( inputType !== '100' ) ? inputType : null;

            // preenchimento das possibilidades de reposta da pergunta.
            // caso o tipo de input seja diferente de 3 ou 5 ( escolha multípla ou dropdown ),
            //     então só tera uma possibilidade
            // caso o tipo de input seja 3 ou 5
            //    então é necessário obter as várias possibilidades e construir
            //    a estrutura a ser enviada para API.
            if ( ![ '3', '4', '5' ].includes( inputType ) ) {
                if ( isChainedScript && fields[`next-question[${question.id}]`] )
                    pages[`${questionKey}[answers][0][unlock_question_position]`] = fields[`next-question[${question.id}]`];
            } else {
                const answers = Object.keys( fields ).filter( key => key.includes( `question[${question.id}]-answer` )
                    && !key.includes( 'next-' ) && !key.includes( 'additional-behaviour-' ) );
                answers.forEach( ( answer, jIndex ) => {
                    // answer = question[questionId]-answer[answerId]
                    if ( isChainedScript )
                        pages[`${questionKey}[answers][${jIndex}][unlock_question_position]`] = fields[`next-${answer}`] || null;

                    if ( fields[answer] )
                        pages[`${questionKey}[answers][${jIndex}][value]`] = fields[answer];

                    if ( fields[`additional-behaviour-${answer}`] )
                        pages[`${questionKey}[answers][${jIndex}][additional_behaviour]`] = +fields[`additional-behaviour-${answer}`];
                });
            }
        });
    },
    edit: ({
        pages,
        script,
        fields,
        scriptType
    }) => {
        const isChainedScript = scriptType === 1;

        script.questions.forEach( ( question, index ) => {
            const questionKey = `questions[${index}]`; // definição da key da questão que será enviada para API.

            // Envia o id da pergunta tanto ao editar como ao apagar pergunta.
            if ( [ 'update', 'delete' ].includes( question.operation ) )
                pages[`${questionKey}[id]`] = question.id;

            // Envia o tipo de operação tanto ao criar como ao editar e ao apagar pergunta.
            pages[`${questionKey}[operation]`] = question.operation;

            // Se for uma criação ou edição, envia a pergunta, a sequência e o tipo de input da resposta caso ela exista.
            if ( [ 'update', 'create' ].includes( question.operation ) ) {
                const newInputType = transformToInt( fields[`type[${transformToInt( question.id )}]`] );
                const oldInputType = question?.type?.id;
                const inputType = newInputType || oldInputType;

                // Envia a questão e o tipo de input.
                pages[`${questionKey}[question]`] = fields[`question[${question.id}]`] || question.question;
                pages[`${questionKey}[input_type_id]`] = ( inputType !== 100 ) ? inputType : null;

                // Se for um guião sequencial, manda a sequência.
                if ( !isChainedScript )
                    pages[`${questionKey}[sequence]`] = index;

                // Se existir alteração do tipo de input e o tipo de input
                // for diferente do tipo de input que existia antes e for uma
                // pergunta que já existia anteriormente, remove todas as
                // respostas associadas e essa pergunta.
                let currentAnswerIndex = -1;
                const answers = Array.isArray( question.nextQuestions ) ? question.nextQuestions : [ question.nextQuestions ];
                if ( newInputType && newInputType !== oldInputType && question.operation === 'update' ) {
                    answers.forEach( ( answer, answerIndex ) => {
                        currentAnswerIndex = answerIndex;
                        pages[`${questionKey}[answers][${answerIndex}][id]`] = answer.idAnswer || answer.id;
                        pages[`${questionKey}[answers][${answerIndex}][operation]`] = 'delete';
                    });
                }

                // se a operation for 'create' ou se a opearation for 'update e o tipo de input tenha mudado
                // -- faz a criação das respostas
                // se a operation for 'update' mas o tipo de input continuar igual
                // --faz o update das respostas
                if ( ( newInputType && newInputType !== oldInputType && question.operation === 'update' )
                || ( ( !newInputType || newInputType === oldInputType ) && question.operation === 'update' && answers.length <= 0 )
                || question.operation === 'create' ) {
                    if ( ![ 3, 4, 5 ].includes( inputType ) ) {
                        if ( fields[`next-question[${question.id}]`] || question?.nextQuestions?.id ) {
                            currentAnswerIndex++;
                            pages[`${questionKey}[answers][${currentAnswerIndex}][operation]`] = 'create';
                            pages[`${questionKey}[answers][${currentAnswerIndex}][value]`] = null;
                        }

                        if ( fields[`next-question[${question.id}]`] ) {
                            // eslint-disable-next-line
                            pages[`${questionKey}[answers][${currentAnswerIndex}][unlock_question_position]`] = fields[`next-question[${question.id}]`];
                        } else if ( question?.nextQuestions?.id )
                            pages[`${questionKey}[answers][${currentAnswerIndex}][unlock_question_id]`] = question?.nextQuestions?.id;
                    } else {
                        // Criação de respostas de perguntas de 'Escolha Múltipla' ou 'Dropdown'
                        const multipleAnswers = Object.keys( fields ).filter( key => key.includes( `question[${question.id}]-answer` )
                        && !key.includes( 'next-' ) && !key.includes( 'additional-behaviour-' ) );

                        multipleAnswers.forEach( answer => {
                            if ( fields[answer] ) {
                                currentAnswerIndex++;
                                pages[`${questionKey}[answers][${currentAnswerIndex}][operation]`] = 'create';
                                if ( isChainedScript ) {
                                    // eslint-disable-next-line
                                    pages[`${questionKey}[answers][${currentAnswerIndex}][unlock_question_position]`] = fields[`next-${answer}`] || null;
                                }
                                pages[`${questionKey}[answers][${currentAnswerIndex}][value]`] = fields[answer] || null;

                                /* eslint-disable */
                                if ( fields.hasOwnProperty( `additional-behaviour-${answer}` ) )
                                    pages[`${questionKey}[answers][${currentAnswerIndex}][additional_behaviour]`] = +fields[`additional-behaviour-${answer}`];
                                /* eslint-enable */
                            }
                        });
                    }
                } else {
                    // Obtém respostas com id (respostas que já existiam)
                    const existingAnswers = answers.map( answer => {
                        const answerId = answer.idAnswer || answer.id;
                        return `question[${question.id}]-answer[${answerId}]`;
                    });

                    answers.forEach( ( answer, jIndex ) => {
                        const answerId = answer.idAnswer || answer.id;

                        // Obtém o id da resposta para eliminar, se existir
                        const isDeleteAnswer = fields[`delete-question[${question.id}]-answer[${answerId}]`];

                        // São definidos o id e a operação da resposta
                        pages[`${questionKey}[answers][${jIndex}][id]`] = answerId;
                        pages[`${questionKey}[answers][${jIndex}][operation]`] = ( !isDeleteAnswer ) ? 'update' : 'delete';

                        // Se não for para eliminar uma resposta, são definidos o resto dos campos
                        if ( !isDeleteAnswer ) {
                            if ( [ 3, 4, 5 ].includes( inputType ) ) {
                                pages[`${questionKey}[answers][${jIndex}][value]`] = fields[`question[${question.id}]-answer[${answerId}]`]
                                || answer.value;

                                if ( fields[`next-question[${question.id}]-answer[${answerId}]`] ) {
                                    // Obtém 'proxima pegunta'
                                    // eslint-disable-next-line
                                    const nextQuestionAnswer = script.questions[fields[`next-question[${question.id}]-answer[${answerId}]`]];

                                    // Verifica se a 'próxima pergunta é uma edição ou criação
                                    // Se for uma edição, obetém-se o id da pergunta e usa-se o unlock_question_id
                                    // Se for uma criação, passa-se a posição da pergunta no array e usa-se o unlock_question_position
                                    const unlockQuestionKey = nextQuestionAnswer.operation === 'create'
                                        ? 'unlock_question_position' : 'unlock_question_id';
                                    const unlockQuestionValue = nextQuestionAnswer.operation === 'create'
                                        ? fields[`next-question[${question.id}]-answer[${answerId}]`] : nextQuestionAnswer.id;

                                    pages[`${questionKey}[answers][${jIndex}][${unlockQuestionKey}]`] = unlockQuestionValue || null;
                                }

                                /* eslint-disable */
                                    if ( fields.hasOwnProperty( `additional-behaviour-question[${question.id}]-answer[${answerId}]` ) )
                                        pages[`${questionKey}[answers][${jIndex}][additional_behaviour]`] = +fields[`additional-behaviour-question[${question.id}]-answer[${answerId}]`];
                                /* eslint-enable */

                                // Respostas de perguntas de 'Escolha Múltipla' para criação
                                const newAnswers = Object.keys( fields ).filter( key => key.includes( `question[${question.id}]-answer` )
                                && !key.includes( 'next-' ) && !key.includes( 'delete-' ) && !key.includes( 'additional-behaviour-' )
                                && !existingAnswers.includes( key ) );

                                newAnswers.forEach( ( newAnswer, lIndex ) => {
                                    if ( fields[newAnswer] ) {
                                        const answerIndex = question.nextQuestions.length + lIndex;
                                        pages[`${questionKey}[answers][${answerIndex}][operation]`] = 'create';
                                        if ( isChainedScript ) {
                                            // eslint-disable-next-line
                                            pages[`${questionKey}[answers][${answerIndex}][unlock_question_position]`] = fields[`next-${newAnswer}`] || null;
                                        }
                                        pages[`${questionKey}[answers][${answerIndex}][value]`] = fields[newAnswer] || null;

                                        /* eslint-disable */
                                            if ( fields.hasOwnProperty( `additional-behaviour-${newAnswer}` ) )
                                                pages[`${questionKey}[answers][${answerIndex}][additional_behaviour]`] = +fields[`additional-behaviour-${newAnswer}`];
                                        /* eslint-enable */
                                    }
                                });
                            } else if ( fields[`next-question[${question.id}]`] || fields[`next-question[${question.id}]`] === '' ) {
                                if ( fields[`next-question[${question.id}]`] === '' )
                                    pages[`${questionKey}[answers][0][unlock_question_id]`] = null;
                                else {
                                    // Obtém 'proxima pegunta'
                                    const nextQuestion = script.questions[fields[`next-question[${question.id}]`]];

                                    // Verifica se a 'próxima pergunta é uma edição ou criação
                                    // Se for uma edição, obetém-se o id da pergunta e usa-se o unlock_question_id
                                    // Se for uma criação, passa-se a posição da pergunta no array e usa-se o unlock_question_position
                                    const unlockQuestionKey = nextQuestion.operation === 'create'
                                        ? 'unlock_question_position' : 'unlock_question_id';
                                    const unlockQuestionValue = nextQuestion.operation === 'create'
                                        ? fields[`next-question[${question.id}]`] : nextQuestion.id;

                                    pages[`${questionKey}[answers][0][${unlockQuestionKey}]`] = unlockQuestionValue || null;
                                }
                            }
                        }
                    });
                }
            }
        });
    }
};

/**
 *
 * Criação do objecto de script.
 * Esta função irá preparar o objecto a ser enviado para API, onde só os campos
 * preenchidos na página é que serão enviados. Assim irá permitir usar esta função
 * na adição e na edição deste modelo.
 *
 * @param {Object}  data
 * @param {Object}  helpers
 * @category model
 * @since 1.1.0
 * @auther Tiago Martinho
 *
*/
const create = ( data, helpers ) => {
    const script = helpers.script || { questions: [] };
    const {
        id,
        scriptName,
        forceUpdate,
        stateId,
        method,
        typeId,
        ...scriptFields
    } = data;

    const pages = {};

    const { isAdd } = router.currentRoute.meta;
    const actionType = isAdd ? 'add' : 'edit';

    buildPages[actionType]({
        pages,
        script,
        fields: scriptFields,
        scriptType: parseInt( typeId, 10 ) || helpers.script?.typeId
    });

    const isEnabled = stateId === 1 ? stateId : 0;

    const call = {
        ...( method && { _method: method }),
        ...( id && { id }),
        ...( forceUpdate && { force_update: +forceUpdate }),
        ...( scriptName && { name: scriptName }),
        ...( isAdd && {
            campaign_id: router.currentRoute.params.campaingId,
            type_id: parseInt( typeId, 10 ),
            enabled: isEnabled
        }),
        ...( isEnabled && { enabled: isEnabled }),
        ...( Object.keys( pages ).length && { ...pages })

    };

    return call;
};

const requiredFields = [
    { field: 'scriptName', isRequiredOnEdit: false },
    { field: 'stateId', isRequiredOnEdit: false },
    { field: 'typeId', isRequiredOnEdit: false }
];

export {
    create,
    requiredFields
};